import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrandLogo } from '../hooks/useBrandLogo';
import axios from 'axios';
import { toast } from 'sonner';
import MobileHeader from '../components/MobileHeader';
import {
  FileSpreadsheet,
  Download,
  Clock,
  User,
  CheckCircle,
  AlertCircle,
  Shield,
  Calendar,
  FileText,
  Users as UsersIcon,
  Ruler,
  ShoppingBag,
  ChevronRight,
  LogOut,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ExportExecutive = () => {
  const navigate = useNavigate();
  const { user, logout, isGerencia } = useAuth();
  const { logoUrl } = useBrandLogo();
  
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadComplete, setDownloadComplete] = useState(false);

  useEffect(() => {
    if (!isGerencia()) {
      navigate('/dashboard');
      toast.error('Acceso restringido a Gerencia');
      return;
    }
    fetchHistory();
  }, [isGerencia, navigate]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/export/audit-history`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.debug('Export history not available:', error.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    setDownloadProgress(0);
    setDownloadComplete(false);
    
    try {
      // Simular progreso mientras se genera
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 20;
        });
      }, 200);

      const response = await axios.get(`${API_URL}/export/executive-report`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(progress);
          }
        }
      });

      clearInterval(progressInterval);
      setDownloadProgress(100);
      setDownloadComplete(true);

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener nombre del archivo del header o generar uno
      const contentDisposition = response.headers['content-disposition'];
      let filename = `JPM_Reporte_Ejecutivo_${new Date().toISOString().slice(0,10)}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Reporte descargado exitosamente');
      
      // Actualizar historial
      setTimeout(() => {
        fetchHistory();
        setDownloadComplete(false);
      }, 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar el reporte');
      setDownloadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-cream">
      <MobileHeader title="Exportación Ejecutiva" />
      
      {/* Desktop Header */}
      <header className="bg-black sticky top-0 z-20 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 py-4">
            <div className="flex items-center gap-5">
              {logoUrl && (
                <img src={logoUrl} alt="Logo" className="h-11 w-auto object-contain brightness-0 invert" />
              )}
              <div>
                <h1 className="font-serif text-xl text-white tracking-wide">ATELIER <span className="text-white/40">—</span> JPM</h1>
                <p className="text-[9px] uppercase tracking-[0.15em] text-white/50">Exportación Ejecutiva</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all rounded-lg">
                Dashboard
              </button>
              <button onClick={handleLogout} className="px-3 py-2 text-white/40 hover:text-white hover:bg-white/10 transition-all rounded-lg">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 text-gold-dark rounded-full text-xs uppercase tracking-wider font-medium mb-4">
            <Shield className="w-4 h-4" />
            Solo Gerencia
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-black mb-3">Exportación Ejecutiva JPM</h1>
          <p className="text-warm-500 max-w-xl mx-auto">
            Genera un reporte premium en Excel con toda la información de clientes, medidas y pedidos, 
            listo para presentación ejecutiva.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl border border-warm-100 shadow-lg overflow-hidden mb-8">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] px-6 md:px-8 py-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
              <FileSpreadsheet className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-serif text-2xl text-white mb-2">Reporte Premium Excel</h2>
            <p className="text-white/70 text-sm">JUAN PABLO MURILLO — Reporte de Clientes Bespoke</p>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="flex items-start gap-3 p-4 bg-warm-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <UsersIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-black">Hoja: Clientes</p>
                  <p className="text-xs text-warm-500 mt-0.5">
                    Nombre, teléfono, email, asesor, VIP
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-warm-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Ruler className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-black">Hoja: Medidas</p>
                  <p className="text-xs text-warm-500 mt-0.5">
                    Cuello, pecho, cintura, manga...
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-warm-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <ShoppingBag className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-black">Hoja: Pedidos</p>
                  <p className="text-xs text-warm-500 mt-0.5">
                    Tipo prenda, tela, precio, estado
                  </p>
                </div>
              </div>
            </div>

            {/* Premium Features */}
            <div className="bg-gradient-to-r from-warm-50 to-amber-50/50 rounded-xl p-5 mb-8 border border-warm-100">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-gold" />
                <span className="text-sm font-medium text-black">Diseño Premium Incluido</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-warm-600">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Encabezados elegantes
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Filas zebra
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Filtros automáticos
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  VIP resaltados ⭐
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Columnas ajustadas
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Encabezados fijos
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Formato moneda
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Fechas formateadas
                </div>
              </div>
            </div>

            {/* Download Button */}
            <div className="text-center">
              {!loading && !downloadComplete ? (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-3 bg-black hover:bg-warm-800 text-white h-14 px-10 rounded-xl text-sm uppercase tracking-wider font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  <Download className="w-5 h-5" />
                  Descargar Reporte Premium
                </button>
              ) : downloadComplete ? (
                <div className="inline-flex items-center gap-3 bg-emerald-500 text-white h-14 px-10 rounded-xl text-sm uppercase tracking-wider font-medium">
                  <CheckCircle className="w-5 h-5" />
                  ¡Descarga Completa!
                </div>
              ) : (
                <div className="inline-flex flex-col items-center gap-3">
                  <div className="flex items-center gap-3 bg-warm-100 text-warm-700 h-14 px-10 rounded-xl text-sm uppercase tracking-wider font-medium">
                    <div className="w-5 h-5 border-2 border-warm-400 border-t-black rounded-full animate-spin" />
                    Generando reporte...
                  </div>
                  {/* Progress Bar */}
                  <div className="w-64 h-2 bg-warm-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-gold to-gold-light transition-all duration-300"
                      style={{ width: `${downloadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-warm-500">{Math.round(downloadProgress)}%</p>
                </div>
              )}
              
              <p className="text-xs text-warm-400 mt-4">
                El archivo se descargará automáticamente en formato .xlsx
              </p>
            </div>
          </div>
        </div>

        {/* Audit History */}
        <div className="bg-white rounded-2xl border border-warm-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-warm-100">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-warm-400" />
              <h3 className="font-serif text-lg text-black">Historial de Exportaciones</h3>
            </div>
          </div>

          {loadingHistory ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-warm-200 border-t-warm-500 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-warm-400">Cargando historial...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-10 h-10 mx-auto mb-3 text-warm-300" />
              <p className="text-warm-500">No hay exportaciones registradas</p>
              <p className="text-xs text-warm-400 mt-1">Las descargas aparecerán aquí</p>
            </div>
          ) : (
            <div className="divide-y divide-warm-100">
              {history.slice(0, 10).map((entry, index) => (
                <div 
                  key={entry.id || index}
                  className="px-6 py-4 flex items-center justify-between hover:bg-warm-50/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-black">Reporte Ejecutivo</p>
                      <p className="text-xs text-warm-500">
                        {entry.records_exported} registros exportados
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-warm-600 flex items-center gap-1.5 justify-end">
                      <User className="w-3.5 h-3.5" />
                      {entry.username}
                    </p>
                    <p className="text-xs text-warm-400">
                      {formatDate(entry.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExportExecutive;
